import * as React from 'react';
import { cn } from '../lib/cn';

/**
 * Card — spec'd in handoff/COMPONENT_SPECS.md.
 *
 * Default surface: white bg, neutral-200 border, 4px radius, shadow-card.
 * Pass `interactive` to opt into the floating-card hover (lifts -4px,
 * upgrades to shadow-card-hover). Reserve for pricing/service/feature
 * cards — not for static info / form / nav surfaces.
 *
 * NOTE: `interactive` defaults to `true` during the migration so
 * marketing pages keep their hover lift. Phase 6 flips it to `false`.
 *
 * Visual-alignment extensions (PORTAL_VISUAL_ALIGNMENT_IMPLEMENTATION_PLAN_2026-05-08.md §VA2.3):
 * - `tone="hairline"` — neutral-200 border, no shadow, brand-glow on hover
 *   (use for feature/marketing-style sections; default cards keep shadow-card)
 * - `featured` — adds brackets-static + top-edge gradient hairline + brand
 *   ring + persistent glow. Use for plan / pricing / promo cards.
 */

type CardTone = 'default' | 'hairline';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  interactive?: boolean;
  /** Visual weight. `default` = current shadow-card; `hairline` = thin border,
   *  no shadow, primary-tinted glow on hover. */
  tone?: CardTone;
  /** Featured cards add corner brackets + a top-edge gradient hairline +
   *  primary ring + persistent glow. Reserve for pricing / promo cards. */
  featured?: boolean;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      interactive = true,
      tone = 'default',
      featured = false,
      children,
      ...props
    },
    ref
  ) => (
    <div
      ref={ref}
      className={cn(
        'relative isolate rounded text-card-foreground',
        tone === 'default' && 'border border-border bg-card shadow-card',
        tone === 'hairline' && 'border border-border bg-card',
        interactive &&
          tone === 'default' &&
          'brackets-hover transition-all duration-300 ease-out cursor-pointer hover:-translate-y-1 hover:border-primary/40 hover:shadow-glow-primary',
        interactive &&
          tone === 'hairline' &&
          'transition-all duration-300 ease-out cursor-pointer hover:-translate-y-1 hover:border-primary/40 hover:shadow-glow-primary',
        featured &&
          'brackets-static border-primary/40 ring-1 ring-primary/20 shadow-glow-primary',
        className
      )}
      {...props}
    >
      {featured && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent"
        />
      )}
      {children}
    </div>
  )
);
Card.displayName = 'Card';

const CardEyebrow = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { index?: number }
>(({ className, index, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'px-6 pt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground',
      className
    )}
    {...props}
  >
    {index !== undefined && (
      <span className="text-primary">{String(index).padStart(2, '0')}/</span>
    )}{' '}
    {children}
  </div>
));
CardEyebrow.displayName = 'CardEyebrow';

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col space-y-1.5 p-6', className)} {...props} />
  )
);
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn('text-xl font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  )
);
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
  )
);
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center p-6 pt-0', className)} {...props} />
  )
);
CardFooter.displayName = 'CardFooter';

export {
  Card,
  CardEyebrow,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
};
export type { CardProps };
